// Sounds for the hoop, all recordings in public/sounds: swish on a make, brick on the rim or backboard,
// airball when it touches nothing, and a streak call after three makes in a row.

interface Clip {
    buffer: AudioBuffer
    start: number
    length: number
}

// trim: cut to the sound itself. whole: play the full recording. first: play only its opening seconds.
type ClipMode = 'trim' | 'whole' | 'first'

interface ClipSource {
    url: string
    mode: ClipMode
    seconds: number
    optional: boolean
    clip: Clip | null
    loading: boolean
}

const clipSource = (url: string, mode: ClipMode, seconds = 0, optional = false): ClipSource =>
    ({ url, mode, seconds, optional, clip: null, loading: false })

const SWISH = clipSource('/sounds/swish.mp3', 'trim', 3)
const BRICK = clipSource('/sounds/brick.mp3', 'trim')
const AIRBALL = clipSource('/sounds/airball.mp3', 'whole')

// Two makes in a row: the first three seconds of the LeBron call.
const LEBRON = clipSource('/sounds/lebron.mp3', 'first', 3)

// Not added yet: once public/sounds/streak.mp3 exists, its first second plays after three makes in a row.
const STREAK = clipSource('/sounds/streak.mp3', 'first', 1, true)

let context: AudioContext | null = null
let noise: AudioBuffer | null = null

const getContext = (): AudioContext | null => {
    if (typeof window === 'undefined') return null

    if (!context) {
        const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

        if (!Ctor) return null

        context = new Ctor()
    }

    return context
}

/*
 * A recording can carry silence before the sound and a long tail after it. This finds
 * the first real sound and where it goes quiet, so a trimmed clip plays just the sound.
 */
const findSound = (buffer: AudioBuffer, maxLength: number): { start: number, length: number } => {
    const data = buffer.getChannelData(0)
    const windowSize = Math.max(1, Math.floor(buffer.sampleRate * 0.02))
    const levels: Array<number> = []

    for (let offset = 0; offset < data.length; offset += windowSize) {
        let sum = 0
        const end = Math.min(data.length, offset + windowSize)

        for (let index = offset; index < end; index++) sum += data[index] * data[index]

        levels.push(Math.sqrt(sum / (end - offset)))
    }

    const peak = Math.max(...levels, 0)

    if (peak === 0) return { start: 0, length: Math.min(buffer.duration, maxLength) }

    const threshold = Math.max(0.01, peak * 0.08)
    const first = Math.max(0, levels.findIndex(level => level >= threshold))
    const quietWindows = Math.round(0.4 / 0.02)
    let last = first
    let quiet = 0

    for (let index = first; index < levels.length; index++) {
        if (levels[index] >= threshold) {
            last = index
            quiet = 0
        }
        else if (++quiet >= quietWindows) break
    }

    const start = Math.max(0, first * 0.02 - 0.01)
    const end = Math.min(buffer.duration, (last + 1) * 0.02 + 0.15)

    return { start, length: Math.min(Math.max(end - start, 0.2), maxLength) }
}

// The brick recording ends with an extra "okay": keep everything up to the last pause before it.
const withoutLastWord = (buffer: AudioBuffer): Clip => {
    const data = buffer.getChannelData(0)
    const windowSize = Math.max(1, Math.floor(buffer.sampleRate * 0.02))
    const levels: Array<number> = []

    for (let offset = 0; offset < data.length; offset += windowSize) {
        let sum = 0
        const end = Math.min(data.length, offset + windowSize)

        for (let index = offset; index < end; index++) sum += data[index] * data[index]

        levels.push(Math.sqrt(sum / (end - offset)))
    }

    const threshold = Math.max(0.01, Math.max(...levels, 0) * 0.05)
    const loud = levels.map((level, index) => level >= threshold ? index : -1).filter(index => index >= 0)

    if (loud.length < 2) return { buffer, start: 0, length: buffer.duration }

    // The "okay" is the last word, after the longest pause in the back part of the recording.
    // Only pauses after 60% of the speech count, so a gap between earlier words is never the cut.
    const speechStart = loud[0]
    const speechEnd = loud[loud.length - 1]
    const earliest = speechStart + (speechEnd - speechStart) * 0.6
    let cutAt = -1
    let longest = 0

    for (let index = 1; index < loud.length; index++) {
        const gap = loud[index] - loud[index - 1]

        if (loud[index - 1] >= earliest && gap > longest) {
            longest = gap
            cutAt = loud[index - 1]
        }
    }

    // No clear pause before a last word: play everything rather than risk cutting speech.
    if (cutAt < 0 || longest < 6) return { buffer, start: 0, length: buffer.duration }

    const end = Math.min(buffer.duration, (cutAt + 1) * 0.02 + 0.15)

    return { buffer, start: 0, length: end }
}

const shapeClip = (buffer: AudioBuffer, source: ClipSource): Clip => {
    if (source === BRICK) return withoutLastWord(buffer)
    if (source.mode === 'whole') return { buffer, start: 0, length: buffer.duration }
    if (source.mode === 'first') return { buffer, start: 0, length: Math.min(source.seconds, buffer.duration) }

    return { buffer, ...findSound(buffer, source.seconds) }
}

const loadClip = (audio: AudioContext, source: ClipSource): void => {
    if (source.clip || source.loading) return

    source.loading = true

    fetch(source.url)
        .then(response => {
            if (response.status === 404 && source.optional) return null
            if (!response.ok) throw new Error(`Sound ${source.url} failed to load (${response.status})`)

            return response.arrayBuffer()
        })
        .then(bytes => bytes ? audio.decodeAudioData(bytes) : null)
        .then(buffer => {
            if (buffer) source.clip = shapeClip(buffer, source)
        })
        .catch(error => {
            source.loading = false
            console.error(error instanceof Error ? error.message : error)
        })
}

// Browsers only allow sound after a tap or click, so every gesture on the uploader calls this.
// On phones the browser only counts the finger lifting as that tap, so the release calls it too.
export const unlockSound = (): void => {
    const audio = getContext()

    if (!audio) return

    // iPhones mute page sound with the silent switch unless the page says it plays media.
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession

    if (session && session.type !== 'playback') {
        try {
            session.type = 'playback'
        }
        catch {
            // Older browsers expose the session read-only; sound still plays with the switch off.
        }
    }
    if (audio.state === 'suspended') audio.resume().catch(() => undefined)

    for (const source of [SWISH, BRICK, AIRBALL, LEBRON, STREAK]) loadClip(audio, source)
}

const playClip = (audio: AudioContext, loaded: Clip, fadeOut: boolean, delay = 0): void => {
    const player = audio.createBufferSource()
    const gain = audio.createGain()
    const now = audio.currentTime + 0.005 + delay

    player.buffer = loaded.buffer
    gain.gain.setValueAtTime(0.9, now)

    // A cut clip fades out at its cut; a whole recording plays to its natural end.
    if (fadeOut) {
        const fade = Math.min(0.25, loaded.length / 3)

        gain.gain.setValueAtTime(0.9, now + loaded.length - fade)
        gain.gain.linearRampToValueAtTime(0.0001, now + loaded.length)
    }

    player.connect(gain).connect(audio.destination)
    player.start(now, loaded.start, loaded.length)
}

const playLoaded = (source: ClipSource, delay = 0): void => {
    const audio = getContext()

    if (!audio || audio.state !== 'running' || !source.clip) return

    playClip(audio, source.clip, source.mode !== 'whole', delay)
}

const noiseBuffer = (audio: AudioContext): AudioBuffer => {
    if (noise) return noise

    const length = Math.floor(audio.sampleRate * 0.6)
    const buffer = audio.createBuffer(1, length, audio.sampleRate)
    const data = buffer.getChannelData(0)

    for (let index = 0; index < length; index++) data[index] = Math.random() * 2 - 1

    noise = buffer

    return buffer
}

// Stand-in used only until the swish recording has loaded: a short burst of filtered noise.
const playSynthSwish = (audio: AudioContext): void => {
    const now = audio.currentTime + 0.01
    const player = audio.createBufferSource()
    const filter = audio.createBiquadFilter()
    const gain = audio.createGain()

    player.buffer = noiseBuffer(audio)
    filter.type = 'bandpass'
    filter.Q.value = 0.8
    filter.frequency.setValueAtTime(6500, now)
    filter.frequency.exponentialRampToValueAtTime(2600, now + 0.24)
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.3, now + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.26)

    player.connect(filter).connect(gain).connect(audio.destination)
    player.start(now)
    player.stop(now + 0.3)
}

export const playSwish = (): void => {
    const audio = getContext()

    if (!audio || audio.state !== 'running') return

    if (SWISH.clip) playClip(audio, SWISH.clip, true)
    else playSynthSwish(audio)
}

// The file clipped the rim or hit the backboard.
export const playBrick = (): void => playLoaded(BRICK)

// The file touched nothing at all.
export const playAirball = (): void => playLoaded(AIRBALL)

// Second make in a row, started just after the swish so both are heard.
export const playLebron = (): void => playLoaded(LEBRON, 0.45)

// Third make in a row, started just after the swish so both are heard.
export const playStreak = (): void => playLoaded(STREAK, 0.45)
